
import { useToast } from "@/hooks/use-toast";

export interface WebSpeechRecognitionProps {
  onTranscriptionComplete: (text: string) => void;
  language: string;
  isListening: boolean;
  setIsListening: (isListening: boolean) => void;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
    mozSpeechRecognition: any;
    msSpeechRecognition: any;
  }
}

export const useWebSpeechRecognition = ({
  onTranscriptionComplete,
  language,
  isListening,
  setIsListening
}: WebSpeechRecognitionProps) => {
  const { toast } = useToast();
  
  const isSupported = () => {
    return !!(
      window.SpeechRecognition ||
      window.webkitSpeechRecognition ||
      window.mozSpeechRecognition ||
      window.msSpeechRecognition
    );
  };

  const createRecognition = () => {
    const SpeechRecognition = 
      window.SpeechRecognition || 
      window.webkitSpeechRecognition || 
      window.mozSpeechRecognition || 
      window.msSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.error('Speech recognition not supported in this browser');
      return null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;

    recognition.onstart = () => {
      console.log('Speech recognition started');
    };

    recognition.onresult = (event: any) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        onTranscriptionComplete(finalTranscript.trim());
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech') {
        setIsListening(false);
        toast({
          title: "Speech Recognition Error",
          description: `Error: ${event.error}. Please try again.`,
          variant: "destructive",
        });
      }
    };

    recognition.onend = () => {
      console.log('Speech recognition ended');
      if (isListening) {
        try {
          recognition.start();
        } catch (error) {
          console.error('Error restarting recognition:', error);
          setIsListening(false);
        }
      }
    };

    return recognition;
  };

  const startRecognition = (recognition: any) => {
    if (!recognition) return;
    
    try {
      recognition.start();
      setIsListening(true);
      
      toast({
        title: "Recording started",
        description: "Speak now...",
      });
    } catch (error) {
      console.error('Speech recognition start error:', error);
      toast({
        title: "Error",
        description: "Failed to start speech recognition. Please reload the page and try again.",
        variant: "destructive",
      });
    }
  };

  const stopRecognition = (recognition: any) => {
    if (!recognition) return;
    
    try {
      recognition.stop();
      setIsListening(false);
      toast({
        title: "Recording stopped",
        description: "Speech recognition has been stopped.",
      });
    } catch (error) {
      console.error('Error stopping recognition:', error);
    }
  };

  return {
    isSupported,
    createRecognition,
    startRecognition,
    stopRecognition
  };
};
